import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Row,
  Col,
  Form,
  Button,
  Card,
  Container,
  Alert,
} from "react-bootstrap";
import { FaPlus, FaTrashAlt, FaExclamationTriangle } from "react-icons/fa";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useTranslation } from "react-i18next";
import SpecialItemsWizard from "./Specialitemswizard";

// Transport types that use the dedicated multi-step wizard instead of the
// generic item-list form below (see SpecialItemsWizard.jsx).
const SPECIAL_WIZARD_TYPES = ["furniture", "building materials", "tires", "moving"];

const DynamicForm = ({ config, onNext, onEditRoute, initialItems = {} }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [items, setItems] = useState([{}]);
  const [fragile, setFragile] = useState("");
  const [itemSecret, setItemSecret] = useState("");
  const [signature, setSignature] = useState("");
  const [fragileDetails, setFragileDetails] = useState("");
  const [specialHandling, setSpecialHandling] = useState("");
  const [specialHandlingDetails, setSpecialHandlingDetails] = useState("");
  const [handlingDetails, setHandlingDetails] = useState("");
  const [helpCarry, setHelpCarry] = useState("");
  const [beladungFloor, setBeladungFloor] = useState("");
  const [entladungFloor, setEntladungFloor] = useState("");
  const [liftBeladung, setLiftBeladung] = useState(false);
  const [liftEntladung, setLiftEntladung] = useState(false);
  const [driving, setDriving] = useState([]);
  const [images, setImages] = useState([]);
  const [description, setDescription] = useState("");

  // حالة الأخطاء
  const [errors, setErrors] = useState({});
  const [showValidationAlert, setShowValidationAlert] = useState(false);

  useEffect(() => {
    if (initialItems && Object.keys(initialItems).length > 0) {
      setItems(
        Array.isArray(initialItems.items) && initialItems.items.length > 0
          ? initialItems.items
          : [{}],
      );
      setFragile(initialItems.fragile || "");
      setItemSecret(initialItems.itemSecret || "");
      setSignature(initialItems.signature || "");
      setFragileDetails(initialItems.fragileDetails || "");
      setSpecialHandling(initialItems.specialHandling || "");
      setSpecialHandlingDetails(initialItems.specialHandlingDetails || "");
      setHandlingDetails(initialItems.handlingDetails || "");
      setHelpCarry(initialItems.helpCarry || "");
      setBeladungFloor(initialItems.beladungFloor || "");
      setEntladungFloor(initialItems.entladungFloor || "");
      setLiftBeladung(initialItems.liftBeladung || false);
      setLiftEntladung(initialItems.liftEntladung || false);
      setDriving(
        Array.isArray(initialItems.driving) ? initialItems.driving : [],
      );
      setImages(initialItems.images || []);
      setDescription(initialItems.description || "");
    }
  }, [initialItems]);

  if (SPECIAL_WIZARD_TYPES.includes(config.type)) {
    return (
      <SpecialItemsWizard
        type={config.type}
        onNext={onNext}
        onEditRoute={onEditRoute}
        initialItems={initialItems?.special_items || initialItems}
      />
    );
  }

  // التحقق من صحة عنصر واحد
  const validateItem = (item, index) => {
    const itemErrors = {};

    config.fields.forEach((field) => {
      if (field.required) {
        const value = item[field.name];
        if (!value || value === "" || value === null || value === undefined) {
          itemErrors[field.name] = t("dynamic_form.field_required", {
            field: t(`dynamic_form.fields.${field.name}`),
          });
        }
      }
    });

    // التحقق من الأبعاد إذا كانت مطلوبة
    if (config.hasDimensions) {
      const hasLength = item.length && item.length !== "";
      const hasWidth = item.width && item.width !== "";
      const hasHeight = item.height && item.height !== "";

      // إذا كان أي من الأبعاد مملوءًا، يجب ملء جميع الأبعاد
      // if (
      //   (hasLength || hasWidth || hasHeight) &&
      //   (!hasLength || !hasWidth || !hasHeight)
      // ) {
      //   itemErrors.dimensions = t("dynamic_form.all_dimensions_required");
      // }
    }

    return itemErrors;
  };

  // التحقق من صحة جميع العناصر
  const validateAllItems = () => {
    const newErrors = {};
    let hasErrors = false;

    items.forEach((item, index) => {
      const itemErrors = validateItem(item, index);
      if (Object.keys(itemErrors).length > 0) {
        newErrors[index] = itemErrors;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  // التحقق من وجود عنصر واحد على الأقل
  const hasAtLeastOneValidItem = () => {
    return items.some((item) => {
      return config.fields.some((field) => {
        const value = item[field.name];
        return value && value !== "" && value !== null && value !== undefined;
      });
    });
  };

  const handleAddItem = () => {
    // التحقق من أن العنصر الحالي ليس فارغًا قبل إضافة عنصر جديد
    const currentItem = items[items.length - 1];
    const hasEmptyFields = config.fields.some((field) => {
      if (field.required) {
        const value = currentItem[field.name];
        return !value || value === "" || value === null || value === undefined;
      }
      return false;
    });

    if (hasEmptyFields) {
      setShowValidationAlert(true);
      // التمرير إلى العنصر الأخير
      const lastItemElement = document.getElementById(
        `item-${items.length - 1}`,
      );
      if (lastItemElement) {
        lastItemElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    const newItem = {};
    config.fields.forEach((field) => {
      newItem[field.name] = field.type === "number" ? "" : "";
    });
    if (config.hasDimensions) {
      newItem.length = "";
      newItem.width = "";
      newItem.height = "";
    }
    if (config.hasDriving) {
      newItem.driving = "";
    }
    setItems((prev) => [...prev, newItem]);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[items.length];
      return newErrors;
    });
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const updated = items.filter((_, i) => i !== index);
      setItems(updated);
      // مسح أخطاء العنصر المحذوف
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        // إعادة ترقيم الأخطاء
        const renumberedErrors = {};
        Object.keys(newErrors).forEach((key) => {
          const newKey =
            parseInt(key) > index ? parseInt(key) - 1 : parseInt(key);
          renumberedErrors[newKey] = newErrors[key];
        });
        return renumberedErrors;
      });
    }
  };

  const handleChange = (index, name, value) => {
    const updated = [...items];
    updated[index][name] = value;
    setItems(updated);

    // مسح الخطأ لهذا الحقل إذا كان موجودًا
    if (errors[index] && errors[index][name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[index]) {
          delete newErrors[index][name];
          if (Object.keys(newErrors[index]).length === 0) {
            delete newErrors[index];
          }
        }
        return newErrors;
      });
    }
  };

  const handleSelect2Change = (index, name, selectedOption) => {
    const updated = [...items];
    updated[index][name] = selectedOption ? selectedOption.value : "";
    setItems(updated);

    // مسح الخطأ لهذا الحقل إذا كان موجودًا
    if (errors[index] && errors[index][name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[index]) {
          delete newErrors[index][name];
          if (Object.keys(newErrors[index]).length === 0) {
            delete newErrors[index];
          }
        }
        return newErrors;
      });
    }
  };

  const handleDrivingChange = (value, isChecked) => {
    setDriving((prev) => {
      if (isChecked) {
        return [...prev, value];
      } else {
        return prev.filter((item) => item !== value);
      }
    });
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setImages((prev) => [...prev, ...files]);
    }
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const getValueOrNull = (value) => {
    return value === "" || value === null || value === undefined ? null : value;
  };

  const getNumericValueOrNull = (value) => {
    const numValue = parseInt(value);
    return isNaN(numValue) || value === "" ? null : numValue.toString();
  };

  const getDefaultValue = (fieldName, value, hasField) => {
    if (!hasField) return null;
    return getValueOrNull(value) || "0";
  };

  const handleSubmit = () => {
    setShowValidationAlert(false);

    // التحقق من وجود عنصر واحد على الأقل
    if (!hasAtLeastOneValidItem()) {
      setShowValidationAlert(true);
      return;
    }

    // التحقق من صحة جميع العناصر
    const isValid = validateAllItems();

    if (!isValid) {
      setShowValidationAlert(true);
      // التمرير إلى أول عنصر به خطأ
      const firstErrorIndex = Object.keys(errors)[0];
      if (firstErrorIndex) {
        const element = document.getElementById(`item-${firstErrorIndex}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    const validItems = items.filter(
      (item) =>
        item &&
        Object.keys(item).some(
          (key) =>
            item[key] !== "" && item[key] !== null && item[key] !== undefined,
        ),
    );

    const isHelpCarryZero = helpCarry === "0";

    const formData = {
      items:
        validItems.length > 0
          ? validItems.map((item, index) => {
              const itemData = { ...item };

              // if (
              //   config.hasDimensions &&
              //   item.length &&
              //   item.width &&
              //   item.height
              // ) {
              //   itemData.dimensions = `${item.length}x${item.width}x${item.height}`;
              // }

              // if (config.hasDimensions) {
              //   itemData.length = item.length || "";
              //   itemData.width = item.width || "";
              //   itemData.height = item.height || "";
              // }

              // if (config.hasDimensions) {
              //   const dimensions = [];
              //   if (item.length && item.length !== "") {
              //     dimensions.push(item.length);
              //     itemData.length = item.length;
              //   }
              //   if (item.width && item.width !== "") {
              //     dimensions.push(item.width);
              //     itemData.width = item.width;
              //   }
              //   if (item.height && item.height !== "") {
              //     dimensions.push(item.height);
              //     itemData.height = item.height;
              //   }
                
              //   // إذا كان هناك قيمة واحدة على الأقل، قم بإنشاء string الأبعاد
              //   if (dimensions.length > 0) {
              //     itemData.dimensions = dimensions.join('x');
              //   } else {
              //     itemData.dimensions = "";
              //   }
              // }

              // if (config.hasDimensions) {
              //   itemData.length = item.length || "";
              //   itemData.width = item.width || "";
              //   itemData.height = item.height || "";
              // }

              if (config.hasDimensions) {
                const length = item.length?.toString().trim() || "";
                const width = item.width?.toString().trim() || "";
                const height = item.height?.toString().trim() || "";
                
                // إذا كان هناك أي قيمة، ندمجها مع `-` للقيم الفارغة
                if (length || width || height) {
                  itemData.dimensions = `${length || "-"}x${width || "-"}x${height || "-"}`;
                } else {
                  itemData.dimensions = null;
                }
                
                // الحفاظ على القيم الفردية مع `-` للقيم الفارغة
                itemData.length = length || "-";
                itemData.width = width || "-";
                itemData.height = height || "-";
              }

              if (config.hasDriving) {
                itemData.driving = item.driving || "";
              }

              return itemData;
            })
          : [{}],

      fragile: getDefaultValue("fragile", fragile, config.hasFragile),
      itemSecret: getDefaultValue(
        "itemSecret",
        itemSecret,
        config.hasItemSecret,
      ),
      signature: getDefaultValue("signature", signature, config.hasSignature),
      fragileDetails: getValueOrNull(fragileDetails),
      specialHandling: getDefaultValue(
        "specialHandling",
        specialHandling,
        config.hasSpecialHandling,
      ),
      handlingDetails: getValueOrNull(handlingDetails),
      helpCarry: getDefaultValue("helpCarry", helpCarry, config.hasHelpCarry),
      beladungFloor: isHelpCarryZero
        ? null
        : getDefaultValue("beladungFloor", beladungFloor, config.hasFloors),
      entladungFloor: isHelpCarryZero
        ? null
        : getDefaultValue("entladungFloor", entladungFloor, config.hasFloors),
      liftBeladung: isHelpCarryZero
        ? null
        : config.hasFloors
          ? liftBeladung
            ? "1"
            : "0"
          : null,
      liftEntladung: isHelpCarryZero
        ? null
        : config.hasFloors
          ? liftEntladung
            ? "1"
            : "0"
          : null,
      images,
      description: getValueOrNull(description),
    };

    onNext(formData);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditRoute = () => {
    if (onEditRoute) {
      onEditRoute();
    } else {
      navigate(-1);
    }
  };

  const getSelectOptions = (options) => {
    return options.length > 0
      ? options?.map((opt) => ({ value: opt, label: t(opt) }))
      : [];
  };

  const getDrivingOptions = () => {
    const drivingField = config.fields.find(
      (field) => field.name === "driving",
    );
    return (
      drivingField?.options || [
        t("dynamic_form.driving_yes"),
        t("dynamic_form.driving_no"),
      ]
    );
  };

  return (
    <Container fluid className="px-0">
      <Card className=" rounded-4 border-0 p-2 p-md-3">
        <h3 className="fw-bold text-center mb-4">{t(config.title)}</h3>

        {/* Alert التحقق */}
        {showValidationAlert && (
          <Alert
            variant="danger"
            className="mb-4"
            onClose={() => setShowValidationAlert(false)}
            dismissible
          >
            <Alert.Heading className="d-flex align-items-center gap-2">
              <FaExclamationTriangle /> {t("dynamic_form.validation_error")}
            </Alert.Heading>
            <p>{t("dynamic_form.please_fill_required_fields")}</p>
            <ul className="mb-0">
              {Object.keys(errors).map((index) => (
                <li key={index}>
                  {t("dynamic_form.item")} {parseInt(index) + 1}:
                  {Object.values(errors[index]).join(", ")}
                </li>
              ))}
              {Object.keys(errors).length === 0 &&
                !hasAtLeastOneValidItem() && (
                  <li>{t("dynamic_form.at_least_one_item_required")}</li>
                )}
            </ul>
          </Alert>
        )}

        <Card className="p-3 p-md-4 mb-4 border-0  rounded-3 bg-light">
          <h5 className="fw-bold mb-3">
            {t("dynamic_form.transport_items")}
            <span className="text-danger ms-2">*</span>
          </h5>

          {items.map((item, index) => (
            <div
              key={index}
              id={`item-${index}`}
              className={`border rounded-3 p-3 mb-3 bg-white  ${errors[index] ? "border-danger" : ""}`}
              style={{ borderColor: errors[index] ? "#dc3545" : "#dee2e6" }}
            >
              {/* عنوان العنصر مع عدد الأخطاء */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">
                  {t("dynamic_form.item")} {index + 1}
                  {errors[index] && (
                    <span className="text-danger ms-2 small">
                      ({Object.keys(errors[index]).length}{" "}
                      {t("dynamic_form.errors")})
                    </span>
                  )}
                </h6>
                {items.length > 1 && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveItem(index)}
                  >
                    <FaTrashAlt /> {t("dynamic_form.remove")}
                  </Button>
                )}
              </div>

              <Row className="mb-3">
                {config.fields.map((field, fIdx) => (
                  <Col xs={12} md={6} lg={6} key={fIdx}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {t(`dynamic_form.fields.${field.name}`)}
                        {field.required && (
                          <span className="text-danger ms-1">*</span>
                        )}
                      </Form.Label>
                      {field.type === "select2" ? (
                        <>
                          <CreatableSelect
                            isClearable
                            options={getSelectOptions(field.options)}
                            value={
                              item[field.name]
                                ? {
                                    value: item[field.name],
                                    label: t(item[field.name]),
                                  }
                                : null
                            }
                            onChange={(selectedOption) =>
                              handleSelect2Change(
                                index,
                                field.name,
                                selectedOption,
                              )
                            }
                            isSearchable={true}
                            placeholder={t("dynamic_form.select_placeholder")}
                            formatCreateLabel={(inputValue) =>
                              t("dynamic_form.create_label", {
                                value: inputValue,
                              })
                            }
                            className={
                              errors[index]?.[field.name] ? "is-invalid" : ""
                            }
                            styles={{
                              control: (base) => ({
                                ...base,
                                backgroundColor: "#fff",
                                borderColor: errors[index]?.[field.name]
                                  ? "#dc3545"
                                  : "#dee2e6",
                                "&:hover": {
                                  borderColor: errors[index]?.[field.name]
                                    ? "#dc3545"
                                    : "#ff6b00",
                                },
                              }),
                              input: (base) => ({
                                ...base,
                                color: "white",
                              }),
                              singleValue: (base) => ({
                                ...base,
                                color: "white",
                              }),
                              placeholder: (base) => ({
                                ...base,
                                color: "#6c757d",
                              }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: "#fff",
                                zIndex: 9999,
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isFocused
                                  ? "#f8f9fa"
                                  : "#fff",
                                color: "#333",
                                "&:hover": {
                                  backgroundColor: "#e9ecef",
                                },
                              }),
                            }}
                          />
                          {errors[index]?.[field.name] && (
                            <Form.Text className="text-danger">
                              {errors[index][field.name]}
                            </Form.Text>
                          )}
                        </>
                      ) : field.type === "select" ? (
                        <>
                          <Form.Select
                            value={item[field.name] || ""}
                            onChange={(e) =>
                              handleChange(index, field.name, e.target.value)
                            }
                            isInvalid={!!errors[index]?.[field.name]}
                          >
                            <option value="">
                              {t("dynamic_form.select_default")}
                            </option>
                            {field.options?.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors[index]?.[field.name]}
                          </Form.Control.Feedback>
                        </>
                      ) : (
                        <>
                          <Form.Control
                            type={field.type}
                            value={item[field.name] || ""}
                            onChange={(e) =>
                              handleChange(index, field.name, e.target.value)
                            }
                            isInvalid={!!errors[index]?.[field.name]}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors[index]?.[field.name]}
                          </Form.Control.Feedback>
                        </>
                      )}
                    </Form.Group>
                  </Col>
                ))}
              </Row>

              {config.hasDimensions &&
                [
                  "palette",
                  "moving",
                  "furniture",
                  "other",
                  "building materials",
                ].includes(config.type) && (
                  <Row className="mb-3 justify-content-center">
                    <Col xs={12} sm={3} md={3}>
                      <Form.Group>
                        <Form.Label>{t("dynamic_form.length")}</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.1"
                          value={item.length || ""}
                          onChange={(e) =>
                            handleChange(index, "length", e.target.value)
                          }
                          // isInvalid={!!errors[index]?.dimensions}
                        />
                      </Form.Group>
                    </Col>
                    <Col
                      xs={1}
                      className="d-flex align-items-end justify-content-center d-none d-sm-flex"
                    >
                      <strong style={{ color: "#ff6b00" }}>X</strong>
                    </Col>
                    <Col xs={12} sm={3} md={3}>
                      <Form.Group>
                        <Form.Label>{t("dynamic_form.width")}</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.1"
                          value={item.width || ""}
                          onChange={(e) =>
                            handleChange(index, "width", e.target.value)
                          }
                          // isInvalid={!!errors[index]?.dimensions}
                        />
                      </Form.Group>
                    </Col>
                    <Col
                      xs={1}
                      className="d-flex align-items-end justify-content-center d-none d-sm-flex"
                    >
                      <strong style={{ color: "#ff6b00" }}>X</strong>
                    </Col>
                    <Col xs={12} sm={3} md={3}>
                      <Form.Group>
                        <Form.Label>{t("dynamic_form.height")}</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.1"
                          value={item.height || ""}
                          onChange={(e) =>
                            handleChange(index, "height", e.target.value)
                          }
                          // isInvalid={!!errors[index]?.dimensions}
                        />
                      </Form.Group>
                    </Col>
                    {/* {errors[index]?.dimensions && (
                      <Col xs={12}>
                        <Form.Text className="text-danger">
                          {errors[index].dimensions}
                        </Form.Text>
                      </Col>
                    )} */}
                  </Row>
                )}

              {config.hasDriving && (
                <div className="p-3">
                  <Row className="mb-3">
                    <Card className="p-3 p-md-4 mb-4 border-0  rounded-3 bg-light">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>
                            {t("dynamic_form.driving_question")}
                            {config.fields.find((f) => f.name === "driving")
                              ?.required && (
                              <span className="text-danger ms-1">*</span>
                            )}
                          </Form.Label>
                          <div className="d-flex flex-column gap-2">
                            <Form.Check
                              type="radio"
                              label={t("dynamic_form.driving_yes")}
                              name={`driving_${index}`}
                              value="1"
                              checked={item.driving === "1"}
                              onChange={(e) =>
                                handleChange(index, "driving", e.target.value)
                              }
                              isInvalid={!!errors[index]?.driving}
                            />
                            <Form.Check
                              type="radio"
                              label={t("dynamic_form.driving_no")}
                              name={`driving_${index}`}
                              value="0"
                              checked={item.driving === "0"}
                              onChange={(e) =>
                                handleChange(index, "driving", e.target.value)
                              }
                              isInvalid={!!errors[index]?.driving}
                            />
                          </div>
                          {errors[index]?.driving && (
                            <Form.Text className="text-danger">
                              {errors[index].driving}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Card>
                  </Row>
                </div>
              )}
            </div>
          ))}

          <div className="text-end mt-3">
            <Button
              variant="outline-warning"
              className="rounded-5 d-flex align-items-center gap-2 ms-auto"
              style={{ border: "1px dashed #ff6b00", color: "#ff6b00" }}
              onClick={handleAddItem}
            >
              <FaPlus /> {t("dynamic_form.add_item")}
            </Button>
          </div>
        </Card>

        {config.hasFragile && (
          <Card className="p-3 p-md-4 mb-4 border-0  rounded-3 bg-light">
            <h5 className="fw-bold mb-3">
              {t("dynamic_form.fragile_question")}
            </h5>
            <div className="d-flex flex-column flex-sm-row gap-2 gap-sm-4 mb-3">
              <Form.Check
                type="radio"
                label={t("dynamic_form.yes")}
                name="fragile"
                value="1"
                checked={fragile === "1"}
                onChange={(e) => setFragile(e.target.value)}
              />
              <Form.Check
                type="radio"
                label={t("dynamic_form.no")}
                name="fragile"
                value="0"
                checked={fragile === "0"}
                onChange={(e) => setFragile(e.target.value)}
              />
            </div>
            {fragile === "1" && (
              <Form.Group>
                <Form.Label>{t("dynamic_form.fragile_details")}</Form.Label>
                <Form.Control
                  value={fragileDetails}
                  onChange={(e) => setFragileDetails(e.target.value)}
                  placeholder={t("dynamic_form.fragile_details_placeholder")}
                />
              </Form.Group>
            )}
          </Card>
        )}

        {/* {config.hasSpecialHandling && (
          <Card className="p-3 p-md-4 mb-4 border-0 shadow-sm rounded-3 bg-light">
            <h5 className="fw-bold mb-3">
              {t("dynamic_form.special_handling_question")}
            </h5>
            <div className="d-flex flex-column flex-sm-row gap-2 gap-sm-4 mb-3">
              <Form.Check
                type="radio"
                label={t("dynamic_form.yes")}
                name="specialHandling"
                value="1"
                checked={specialHandling === "1"}
                onChange={(e) => setSpecialHandling(e.target.value)}
              />
              <Form.Check
                type="radio"
                label={t("dynamic_form.no")}
                name="specialHandling"
                value="0"
                checked={specialHandling === "0"}
                onChange={(e) => setSpecialHandling(e.target.value)}
              />
            </div>
            {specialHandling === "1" && (
              <Form.Group>
                <Form.Label>
                  {t("dynamic_form.special_handling_details")}
                </Form.Label>
                <Form.Control
                  value={handlingDetails}
                  name="specialHandlingDetails"
                  onChange={(e) => setHandlingDetails(e.target.value)}
                  placeholder={t(
                    "dynamic_form.special_handling_details_placeholder",
                  )}
                />
              </Form.Group>
            )}
          </Card>
        )} */}

        {config.hasHelpCarry && (
          <Card className="p-3 p-md-4 mb-4 border-0  rounded-3 bg-light">
            <h5 className="fw-bold mb-3">
              {t("dynamic_form.help_carry_question")}
            </h5>
            <div className="d-flex flex-column flex-sm-row justify-content-between gap-2">
              <Form.Check
                type="radio"
                name="helpCarry"
                label={t("dynamic_form.no_help")}
                value="0"
                checked={helpCarry === "0"}
                onChange={(e) => setHelpCarry(e.target.value)}
              />
              <Form.Check
                type="radio"
                name="helpCarry"
                label={t("dynamic_form.partial_help")}
                value="1"
                checked={helpCarry === "1"}
                onChange={(e) => setHelpCarry(e.target.value)}
              />
              <Form.Check
                type="radio"
                name="helpCarry"
                label={t("dynamic_form.full_help")}
                value="2"
                checked={helpCarry === "2"}
                onChange={(e) => setHelpCarry(e.target.value)}
              />
            </div>
          </Card>
        )}

        {config.hasFloors && helpCarry !== "0" && (
          <Card className="p-3 p-md-4 mb-4 border-0  rounded-3 bg-light">
            <h5 className="fw-bold mb-3">{t("dynamic_form.floors")}</h5>
            <Row>
              <Col xs={12} md={6} className="mb-3 mb-md-0">
                <Form.Group>
                  <Form.Label>{t("dynamic_form.loading_floor")}</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={beladungFloor}
                    onChange={(e) => setBeladungFloor(e.target.value)}
                    placeholder="0"
                  />
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  label={t("dynamic_form.elevator_available")}
                  checked={liftBeladung}
                  onChange={(e) => setLiftBeladung(e.target.checked)}
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>{t("dynamic_form.unloading_floor")}</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={entladungFloor}
                    onChange={(e) => setEntladungFloor(e.target.value)}
                    placeholder="0"
                  />
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  label={t("dynamic_form.elevator_available")}
                  checked={liftEntladung}
                  onChange={(e) => setLiftEntladung(e.target.checked)}
                />
              </Col>
            </Row>
          </Card>
        )}

        <Card className="p-3 p-md-4 mb-4 border-0  rounded-3 bg-light">
          <h5 className="fw-bold mb-3">{t("dynamic_form.photos")}</h5>
          <Form.Group className="mb-3">
            <Form.Label>{t("dynamic_form.add_photos")}</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
            />
            <Form.Text className="text-muted">
              {t("dynamic_form.photos_hint")}
            </Form.Text>
          </Form.Group>

          {images.length > 0 && (
            <div className="mt-3">
              <h6>{t("dynamic_form.selected_photos")}</h6>
              <Row>
                {images.map((image, index) => (
                  <Col
                    xs={6}
                    sm={4}
                    md={3}
                    key={index}
                    className="mb-2 position-relative"
                  >
                    {image instanceof File ? (
                      <img
                        src={URL.createObjectURL(image)}
                        alt={t("dynamic_form.upload_alt", { index: index + 1 })}
                        style={{
                          width: "100%",
                          height: "80px",
                          objectFit: "cover",
                        }}
                        className="rounded"
                      />
                    ) : (
                      <img
                        src={image}
                        alt={t("dynamic_form.upload_alt", { index: index + 1 })}
                        style={{
                          width: "100%",
                          height: "80px",
                          objectFit: "cover",
                        }}
                        className="rounded"
                        onError={(e) => {
                          console.error("Error loading image:", image);
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeImage(index)}
                      style={{ transform: "translate(50%, -50%)" }}
                    >
                      ×
                    </Button>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Card>

        <Card className="p-3 p-md-4 mb-4 border-0  rounded-3 bg-light">
          <h5 className="fw-bold mb-3">{t("dynamic_form.additional_notes")}</h5>
          <Form.Group>
            <Form.Label>{t("dynamic_form.description")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder={t("dynamic_form.description_placeholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>
        </Card>

        <div className="d-flex flex-column flex-sm-row justify-content-between gap-3 py-3">
          <Button
            variant="secondary"
            className="px-4 rounded-5 order-2 order-sm-1"
            onClick={handleEditRoute}
          >
            {t("dynamic_form.back")}
          </Button>
          <Button
            variant="secondary"
            className="px-4 rounded-5 order-1 order-sm-2"
            style={{ backgroundColor: "#ff6b00", border: "none" }}
            onClick={handleSubmit}
          >
            {t("dynamic_form.next")}
          </Button>
        </div>
      </Card>
    </Container>
  );
};

export default DynamicForm;